import React, { useId } from 'react';

import CheckIcon from '@/assets/check.svg?react';
import ErrorMessage from '@/components/ui/ErrorMessage';
import cn from '@/utils/cn';

import { Root } from '@radix-ui/react-checkbox';

interface Props extends React.ComponentPropsWithoutRef<typeof Root> {
  label: string | JSX.Element;
  error?: string;
  variant?: 'default' | 'filled';
  classNameBox?: string;
  classNameLabel?: string;
}

const Checkbox = React.forwardRef<React.ElementRef<typeof Root>, Props>(
  (
    {
      label,
      error,
      variant = 'default',
      id,
      className,
      classNameBox,
      classNameLabel,
      ...props
    },
    ref
  ) => {
    const idGen = useId();

    return (
      <>
        <Root
          id={id ?? idGen}
          ref={ref}
          className={cn(
            'group flex items-center gap-2.5 transition-colors',
            variant === 'default' && 'max-w-max',
            variant === 'filled' &&
              'w-full rounded-lg p-4 outline outline-1 outline-dark-gray-100 data-[state=checked]:outline-green-500 md:py-[16px]',
            variant === 'filled' && 'data-[state=checked]:bg-green-50 ',
            props.disabled && 'cursor-not-allowed',
            'duration-200 ease-in-out',
            className
          )}
          {...props}
        >
          <div
            className={cn(
              'flex aspect-square items-center justify-center rounded outline outline-1 outline-gray-400 transition-[background-color,outline-color] group-data-[state=checked]:bg-green-500 group-data-[state=checked]:text-gray-100 group-data-[state=checked]:outline-green-500 [&>svg>path]:fill-gray-100 [&>svg>path]:duration-200',
              variant === 'default' &&
                'w-6 group-hover:outline-green-500 lg:w-4.5',
              variant === 'filled' &&
                'size-4.5 bg-white group-hover:[&>svg>path]:fill-gray-500',
              'group-data-[state=checked]:[&>svg>path]:fill-white',
              error && variant === 'default' && 'outline-red-400',
              props.disabled &&
                'cursor-not-allowed !outline-gray-400 group-hover:[&>svg>path]:fill-white',
              'duration-200 ease-in-out',
              classNameBox
            )}
          >
            <CheckIcon className="size-6 lg:size-4.5" />
          </div>
          <label
            htmlFor={id ?? idGen}
            className={cn(
              'caption-large cursor-pointer text-left text-dark-gray-500 transition-colors',
              variant === 'filled' &&
                'group-data-[state=checked]:text-green-500',
              props.disabled && 'cursor-not-allowed',
              classNameLabel
            )}
          >
            {label}
          </label>
        </Root>
        {error && <ErrorMessage errorMessage={error} />}
      </>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
